import { useTranslation } from "react-i18next";
import { Layout } from "@/components/Layout";
import { CONTACT } from "@/lib/contact";

const LAST_UPDATED = "5 de julio de 2026";

export default function Privacy() {
  const { t, i18n } = useTranslation();
  const isSpanish = i18n.language === "es";
  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="as-label text-as-green mb-3">{t("privacy.eyebrow")}</p>
          <h1 className="as-heading text-4xl sm:text-6xl text-white leading-tight" data-testid="privacy-title">
            {t("privacy.title_1")} <span className="text-as-green">{t("privacy.title_2")}</span>
          </h1>
          <p className="text-as-gray mt-4 max-w-2xl text-sm">
            {t("privacy.last_updated_label")} <span className="text-white">{LAST_UPDATED}</span>
          </p>
          {!isSpanish && (
            <p
              className="mt-4 max-w-2xl text-xs text-as-gray-dim border-l-2 border-as-green pl-3"
              data-testid="privacy-translation-notice"
            >
              {t("privacy.note_es_source")}
            </p>
          )}
        </div>
      </section>

      <article className="bg-as-surface py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 text-as-gray leading-relaxed">
          <Section title={t("privacy.s1_title")}>
            <p>
              AS TRAINING (en adelante, <b className="text-white">"AS TRAINING"</b>, <b className="text-white">"nosotros"</b> o <b className="text-white">"la plataforma"</b>) es un servicio profesional
              de coaching en nutrición y entrenamiento personalizado, operado por <b className="text-white">Fabricio Alves de Almeida</b>,
              disponible en <a href="https://www.astraining2020.com" className="text-as-green">www.astraining2020.com</a>.
            </p>
            <p>
              Esta política explica qué datos personales recopilamos, cómo los usamos, con quién los compartimos y qué
              derechos tienes sobre ellos. Al utilizar la plataforma aceptas las prácticas descritas aquí.
            </p>
          </Section>

          <Section title={t("privacy.s2_title")}>
            <p>Recopilamos los siguientes datos personales, siempre bajo el principio de <b className="text-white">minimización</b>:</p>
            <ul className="space-y-2 list-disc list-inside marker:text-as-green">
              <li><b className="text-white">Datos de cuenta</b>: nombre, email, foto de perfil e identificador de Google (proporcionados por Google al iniciar sesión).</li>
              <li><b className="text-white">Datos de evaluación física y de salud</b>: edad, género, altura, peso, medidas corporales, objetivo, nivel de entrenamiento, historial médico relevante, hábitos alimenticios y fotos de progreso (si eliges subirlas).</li>
              <li><b className="text-white">Datos de seguimiento</b>: registros semanales de peso, medidas, porcentaje de grasa y observaciones que voluntariamente registres.</li>
              <li><b className="text-white">Datos de plan y pago</b>: plan contratado (AS START, AS EVOLUTION, AS ELITE), fecha de activación, fecha de vencimiento e identificadores de transacción emitidos por Stripe.</li>
              <li><b className="text-white">Datos técnicos</b>: dirección IP, tipo de navegador, sistema operativo y timestamps de tus sesiones, únicamente para seguridad y prevención de fraude.</li>
            </ul>
            <p className="text-sm">
              <b className="text-white">Datos sensibles de salud</b>: los datos de tu evaluación (peso, medidas, condiciones médicas) son
              tratados con el máximo nivel de confidencialidad. Solo son accesibles por Fabricio Alves de Almeida como coach y por ti.
            </p>
          </Section>

          <Section title={t("privacy.s3_title")}>
            <p>
              Utilizamos <b className="text-white">Google Sign-In</b> a través de la infraestructura de autenticación
              gestionada por Emergent. Cuando inicias sesión, Google nos comparte tu nombre, email y foto de perfil.
              No recibimos tu contraseña de Google y no la almacenamos en ningún momento. Puedes revocar el acceso
              en cualquier momento desde tu cuenta de Google (<a href="https://myaccount.google.com/permissions" className="text-as-green" target="_blank" rel="noreferrer">Permisos de terceros</a>).
            </p>
          </Section>

          <Section title={t("privacy.s4_title")}>
            <p>
              Los planes personalizados (entrenamiento, alimentación, suplementación) se generan a partir de la información
              que voluntariamente registras en tu evaluación inicial. Esta información nos permite:
            </p>
            <ul className="space-y-2 list-disc list-inside marker:text-as-green">
              <li>Diseñar tu plan personalizado.</li>
              <li>Ajustar el plan durante las revisiones semanales/mensuales.</li>
              <li>Generar los PDFs profesionales (Guía de Ejercicios, Guía de Alimentación, Guía de Suplementación).</li>
              <li>Enviarte el Pack de Bienvenida y los recursos incluidos en tu plan.</li>
            </ul>
            <p className="text-sm">
              Tu plan y evaluación NO se comparten con otros clientes. Nuestros catálogos de ejercicios, nutrición y suplementación
              son contenido educativo genérico y no constituyen consejo médico.
            </p>
          </Section>

          <Section title={t("privacy.s5_title")}>
            <p>
              Los pagos se procesan por <b className="text-white">Stripe, Inc.</b>, un procesador de pagos certificado PCI-DSS Nivel 1.
              AS TRAINING <b className="text-white">nunca almacena</b> tu número de tarjeta, CVV ni fecha de vencimiento —
              esos datos viajan directamente entre tu navegador y los servidores de Stripe.
            </p>
            <p>
              Nosotros solo almacenamos el identificador de la transacción, el monto, el plan adquirido y el estado del pago.
              La política de privacidad completa de Stripe está disponible en <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="text-as-green">stripe.com/privacy</a>.
            </p>
            <p className="text-sm">
              Adicionalmente, aceptamos pagos manuales (Zelle, Venmo). En estos casos solo registramos el plan asignado
              y la fecha de vencimiento; no recopilamos datos bancarios.
            </p>
          </Section>

          <Section title={t("privacy.s6_title")}>
            <p>
              Enviamos los siguientes correos vía <b className="text-white">Resend</b>:
            </p>
            <ul className="space-y-2 list-disc list-inside marker:text-as-green">
              <li>Correo de bienvenida cuando activas un plan AS EVOLUTION o AS ELITE (con enlace de descarga al Pack de Bienvenida).</li>
              <li>Notificaciones administrativas internas (recibidas únicamente por Fabricio Alves de Almeida).</li>
            </ul>
            <p className="text-sm">
              No realizamos envíos de marketing masivo. Cualquier correo de AS TRAINING que recibas está relacionado
              directamente con el servicio que contrataste.
            </p>
          </Section>

          <Section title={t("privacy.s7_title")}>
            <p>
              Tus datos se almacenan en <b className="text-white">MongoDB Atlas</b> (base de datos cifrada en reposo con AES-256)
              en infraestructura Kubernetes gestionada por Emergent. La comunicación entre tu navegador y nuestros servidores
              utiliza siempre TLS 1.3 (HTTPS). Aplicamos control de acceso basado en roles: solo Fabricio Alves de Almeida como administrador
              puede ver los datos de todos los clientes; el resto de usuarios solo accede a sus propios datos.
            </p>
            <p className="text-sm">
              Realizamos copias de seguridad periódicas. En caso de una brecha de seguridad que afecte tus datos personales,
              te notificaremos por email dentro de las 72 horas siguientes conforme a estándares internacionales (GDPR / LGPD).
            </p>
          </Section>

          <Section title={t("privacy.s8_title")}>
            <p>Compartimos datos mínimos y necesarios con estos proveedores:</p>
            <ul className="space-y-2 list-disc list-inside marker:text-as-green">
              <li><b className="text-white">Google</b> (autenticación) — recibe nada nuevo; solo se autentica el email.</li>
              <li><b className="text-white">Stripe</b> (pagos) — recibe tu método de pago y monto de la transacción.</li>
              <li><b className="text-white">Resend</b> (envío de correos) — recibe tu email y el contenido del mensaje.</li>
              <li><b className="text-white">Emergent</b> (infraestructura Kubernetes) — hospeda la plataforma y las bases de datos.</li>
            </ul>
            <p className="text-sm">
              No vendemos, alquilamos ni cedemos tus datos personales a terceros con fines publicitarios o de marketing.
            </p>
          </Section>

          <Section title={t("privacy.s9_title")}>
            <p>Como usuario tienes derecho a:</p>
            <ul className="space-y-2 list-disc list-inside marker:text-as-green">
              <li><b className="text-white">Acceder</b> a los datos que tenemos sobre ti.</li>
              <li><b className="text-white">Rectificar</b> datos incorrectos o desactualizados.</li>
              <li><b className="text-white">Eliminar</b> tu cuenta y todos tus datos personales asociados.</li>
              <li><b className="text-white">Exportar</b> tus datos en formato JSON legible.</li>
              <li><b className="text-white">Retirar el consentimiento</b> en cualquier momento (esto puede impedir el uso del servicio).</li>
              <li><b className="text-white">Presentar una reclamación</b> ante la autoridad de protección de datos de tu país.</li>
            </ul>
            <p className="text-sm">
              Para ejercer cualquiera de estos derechos, escríbenos a <a href={`mailto:${CONTACT.email}`} className="text-as-green">{CONTACT.email}</a>.
              Responderemos en un plazo máximo de 30 días.
            </p>
          </Section>

          <Section title={t("privacy.s10_title")}>
            <p>
              Conservamos tus datos mientras tu cuenta esté activa. Si cancelas tu cuenta o solicitas eliminación,
              borramos todos tus datos personales dentro de 30 días. Excepción: podemos conservar registros de transacciones
              de pago hasta por 5 años por obligaciones fiscales aplicables.
            </p>
          </Section>

          <Section title={t("privacy.s11_title")}>
            <p>
              Usamos cookies estrictamente necesarias para mantener tu sesión iniciada (<code className="text-as-green">session_token</code>).
              También utilizamos <code className="text-as-green">localStorage</code> del navegador para guardar tus preferencias
              de accesibilidad (tamaño de fuente, alto contraste, etc.). No usamos cookies de rastreo publicitario ni
              cookies de terceros con fines de marketing.
            </p>
          </Section>

          <Section title={t("privacy.s12_title")}>
            <p>
              AS TRAINING no está dirigido a menores de 16 años. No recopilamos datos de menores de forma intencional.
              Si eres padre/madre/tutor y detectas que un menor a tu cargo ha creado una cuenta, escríbenos y eliminaremos
              la cuenta y todos los datos asociados.
            </p>
          </Section>

          <Section title={t("privacy.s13_title")}>
            <p>
              Podemos actualizar esta política ocasionalmente. Cambios sustanciales se notificarán por email o mediante
              un aviso destacado en la plataforma antes de entrar en vigor. La fecha de "Última actualización" en la parte
              superior de esta página siempre refleja la versión vigente.
            </p>
          </Section>

          <Section title={t("privacy.s14_title")}>
            <div className="border border-as-border p-6 bg-as-black">
              <p className="text-white mb-2">Para consultas de privacidad, ejercicio de derechos o dudas legales:</p>
              <p className="text-lg">
                <a href={`mailto:${CONTACT.email}`} className="text-as-green font-bold" data-testid="privacy-contact-email">
                  {CONTACT.email}
                </a>
              </p>
              <p className="text-sm text-as-gray mt-3">
                Responsable del tratamiento: Fabricio Alves de Almeida — AS TRAINING · <a href={`https://wa.me/${CONTACT.phoneIntl}`} className="text-as-green" target="_blank" rel="noreferrer">WhatsApp {CONTACT.phone}</a>
              </p>
            </div>
          </Section>
        </div>
      </article>
    </Layout>
  );
}

function Section({ title, children }) {
  const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return (
    <section aria-labelledby={`priv-${id}`}>
      <h2 id={`priv-${id}`} className="as-heading text-2xl sm:text-3xl text-white mb-4 border-l-4 border-as-green pl-4">
        {title}
      </h2>
      <div className="space-y-3 text-sm sm:text-base">{children}</div>
    </section>
  );
}
